import { AxiosPromise, AxiosResponse } from 'axios';
import httpClient from './httpClient';
import HttpRequestMethods from './httpRequestMethods';
import UrlConfig from '../interfaces/UrlConfig';
import RequestConfig from '../interfaces/RequestConfig';

function buildCustomizedConfig(
  currConfig: RequestConfig,
  urlConfig: UrlConfig
): RequestConfig {
  const config = { ...currConfig };

  // set withCredentials
  if (urlConfig.withCredentials) {
    config.withCredentials = urlConfig.withCredentials;
  }
  // enable retry logic for fail request
  if (urlConfig.retryable) {
    config.retryable = urlConfig.retryable;
  }
  // disable cache
  if (urlConfig.noCache) {
    config.headers = {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: 0
    };
  }
  // append header argument
  if (urlConfig.headers) {
    config.headers = {
      ...config.headers,
      ...urlConfig.headers
    };
  }

  return config;
}

function buildRequest(config: RequestConfig, urlConfig: UrlConfig): AxiosPromise {
  if (!urlConfig) {
    Promise.reject(new Error('No config found'));
  }
  return httpClient(buildCustomizedConfig(config, urlConfig));
}

function buildGetRequest(
  urlConfig: UrlConfig,
  params?: URLSearchParams | object
): AxiosPromise {
  return buildRequest(
    {
      method: HttpRequestMethods.GET,
      url: urlConfig.url,
      params
    },
    urlConfig
  );
}

function buildPostRequest(
  urlConfig: UrlConfig,
  data?: Document | BodyInit | object | null
): AxiosPromise {
  return buildRequest(
    {
      method: HttpRequestMethods.POST,
      url: urlConfig.url,
      data
    },
    urlConfig
  );
}

function buildPatchRequest(
  urlConfig: UrlConfig,
  data?: Document | BodyInit | null
): AxiosPromise {
  return buildRequest(
    {
      method: HttpRequestMethods.PATCH,
      url: urlConfig.url,
      data
    },
    urlConfig
  );
}

function buildDeleteRequest(urlConfig: UrlConfig): AxiosPromise {
  return buildRequest(
    {
      method: HttpRequestMethods.DELETE,
      url: urlConfig.url
    },
    urlConfig
  );
}

function buildBatchPromises(
  arrayNeedsBatch: string[],
  cutOff: number,
  urlConfig: UrlConfig,
  isPost: boolean
): Promise<AxiosResponse[]> {
  const promises: AxiosPromise[] = [];
  let startIndex = 0;
  let subArray = arrayNeedsBatch.slice(startIndex, cutOff);
  while (subArray.length > 0) {
    const params = {
      userIds: subArray
    };
    if (isPost) {
      promises.push(buildPostRequest(urlConfig, params));
    } else {
      promises.push(buildGetRequest(urlConfig, params));
    }
    startIndex += 1;
    subArray = arrayNeedsBatch.slice(startIndex * cutOff, startIndex * cutOff + cutOff);
  }
  return Promise.all(promises);
}

export default {
  methods: HttpRequestMethods,
  get: buildGetRequest,
  post: buildPostRequest,
  delete: buildDeleteRequest,
  patch: buildPatchRequest,
  buildBatchPromises
};
